import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  Plus, Edit, Trash2, Search, X, ShieldCheck, UserCog, KeyRound, Lock, Mail, Phone
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = {
  name: '', email: '', password: '', phone: '', address: '',
  roleId: 'none', status: true
};

const Admins = () => {
  const { admin: currentAdmin } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [current, setCurrent] = useState(null);
  const [formData, setFormData] = useState(initialForm);

  const fetchAll = async () => {
    try {
      const [a, r] = await Promise.all([
        axios.get(`${API}/admins`),
        axios.get(`${API}/roles`)
      ]);
      setAdmins(a.data || []);
      setRoles(r.data || []);
    } catch (err) {
      toast.error('Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return admins.filter((a) => {
      if (roleFilter !== 'all') {
        if (roleFilter === 'none' && a.roleId) return false;
        if (roleFilter !== 'none' && String(a.roleId || '') !== roleFilter) return false;
      }
      if (statusFilter !== 'all') {
        const isActive = !!a.status;
        if (statusFilter === 'active' && !isActive) return false;
        if (statusFilter === 'inactive' && isActive) return false;
      }
      if (q) {
        const hay = [a.name, a.email, a.phone, a.role?.name].filter(Boolean).map((v) => String(v).toLowerCase());
        if (!hay.some((v) => v.includes(q))) return false;
      }
      return true;
    });
  }, [admins, search, roleFilter, statusFilter]);

  const stats = useMemo(() => {
    const total = admins.length;
    const active = admins.filter((a) => a.status).length;
    const withRole = admins.filter((a) => a.roleId).length;
    return { total, active, inactive: total - active, withRole };
  }, [admins]);

  const openCreate = () => {
    setEditMode(false);
    setCurrent(null);
    setFormData({ ...initialForm });
    setShowDialog(true);
  };

  const openEdit = (a) => {
    setEditMode(true);
    setCurrent(a);
    setFormData({
      name: a.name || '',
      email: a.email || '',
      password: '',
      phone: a.phone || '',
      address: a.address || '',
      roleId: a.roleId || 'none',
      status: !!a.status
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (payload.roleId === 'none') payload.roleId = null;
      if (editMode && !payload.password) delete payload.password;

      if (editMode && current) {
        await axios.patch(`${API}/admins/${current.id}`, payload);
        toast.success('Admin updated');
      } else {
        await axios.post(`${API}/admins`, payload);
        toast.success('Admin created');
      }
      setShowDialog(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save admin');
    }
  };

  const remove = async (a) => {
    if (a.id === currentAdmin?.id) {
      toast.error("You can't delete your own account");
      return;
    }
    if (!window.confirm(`Delete admin "${a.name}"?`)) return;
    try {
      await axios.delete(`${API}/admins/${a.id}`);
      toast.success('Admin deleted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const toggleStatus = async (a) => {
    try {
      await axios.patch(`${API}/admins/${a.id}`, { status: !a.status });
      toast.success(`Admin ${!a.status ? 'activated' : 'deactivated'}`);
      fetchAll();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8" style={{ fontFamily: 'Public Sans, sans-serif' }}>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#0F172A' }}>
            <UserCog size={26} className="text-orange-600" /> Admins
          </h1>
          <p className="text-gray-600 text-sm mt-1">Manage portal users and assign their roles</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button data-testid="create-admin-button" onClick={openCreate} style={{ backgroundColor: '#C2410C' }} className="text-white">
              <Plus size={18} className="mr-2" />New Admin
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="admin-dialog">
            <DialogHeader>
              <DialogTitle>{editMode ? `Edit Admin: ${current?.name}` : 'Create New Admin'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input data-testid="admin-name-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input data-testid="admin-email-input" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
                </div>
              </div>
              <div>
                <Label>{editMode ? 'New Password (leave blank to keep current)' : 'Password *'}</Label>
                <Input
                  data-testid="admin-password-input"
                  type="password"
                  placeholder={editMode ? '••••••••' : 'min 6 characters'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editMode}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Phone</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                </div>
                <div>
                  <Label>Address</Label>
                  <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Role</Label>
                <Select value={formData.roleId} onValueChange={(v) => setFormData({ ...formData, roleId: v })}>
                  <SelectTrigger data-testid="admin-role-select"><SelectValue placeholder="Select a role" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— No specific role —</SelectItem>
                    {roles.map((r) => (
                      <SelectItem key={r.id} value={r.id} data-testid={`admin-role-option-${r.id}`}>
                        {r.name}{r.isSystem ? ' (System)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Permissions come live from the selected role — editing the role later updates this admin immediately.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  data-testid="admin-status-switch"
                  id="admin-status"
                  checked={formData.status}
                  onCheckedChange={(v) => setFormData({ ...formData, status: v })}
                />
                <Label htmlFor="admin-status" className="!m-0">Account Active</Label>
              </div>
              <Button type="submit" className="w-full" style={{ backgroundColor: '#C2410C' }}>
                {editMode ? 'Save Changes' : 'Create Admin'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* STAT CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="admins-stats">
        {[
          { label: 'Total Admins', value: stats.total,    icon: UserCog,     color: '#7C3AED', bg: '#F5F3FF' },
          { label: 'Active',       value: stats.active,   icon: ShieldCheck, color: '#059669', bg: '#ECFDF5' },
          { label: 'Inactive',     value: stats.inactive, icon: Lock,        color: '#9CA3AF', bg: '#F3F4F6' },
          { label: 'With Role',    value: stats.withRole, icon: KeyRound,    color: '#C2410C', bg: '#FFF7ED' }
        ].map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2.5 rounded-lg" style={{ backgroundColor: c.bg }}>
                  <Icon size={20} style={{ color: c.color }} />
                </div>
              </div>
              <h3 className="text-2xl font-bold" style={{ color: '#0F172A' }}>{c.value}</h3>
              <p className="text-sm text-gray-600 mt-1">{c.label}</p>
            </div>
          );
        })}
      </div>

      {/* FILTER BAR */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="admins-filter-bar">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            data-testid="admins-search-input"
            placeholder="Search name, email, phone, role..."
            className="pl-9 pr-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X size={16} />
            </button>
          )}
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[180px]" data-testid="admins-role-filter"><SelectValue placeholder="Role" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="none">No role</SelectItem>
            {roles.map((r) => (<SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px]" data-testid="admins-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        {(search || roleFilter !== 'all' || statusFilter !== 'all') && (
          <Button variant="outline" size="sm" data-testid="admins-clear-filters" onClick={() => { setSearch(''); setRoleFilter('all'); setStatusFilter('all'); }}>
            Clear filters
          </Button>
        )}
        <div className="ml-auto text-sm text-gray-600">
          {filtered.length} of {admins.length}
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="admins-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-600 uppercase">Admin</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-600 uppercase">Email</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-600 uppercase">Phone</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-600 uppercase">Role</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-600 uppercase">Status</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filtered.map((a) => {
                const isSelf = a.id === currentAdmin?.id;
                return (
                  <tr key={a.id} className="hover:bg-gray-50" data-testid={`admin-row-${a.id}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-full bg-orange-100 text-orange-700 font-bold flex items-center justify-center text-sm">
                          {(a.name || a.email || '?').slice(0, 1).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 flex items-center gap-2">
                            {a.name || 'N/A'}
                            {isSelf && <Badge className="bg-blue-100 text-blue-700 border-0 text-[10px]">You</Badge>}
                          </p>
                          {a.address && <p className="text-xs text-gray-500">{a.address}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-gray-700">
                      <span className="flex items-center gap-1"><Mail size={12} className="text-gray-400" />{a.email}</span>
                    </td>
                    <td className="px-5 py-3 text-gray-700">
                      {a.phone ? <span className="flex items-center gap-1"><Phone size={12} className="text-gray-400" />{a.phone}</span> : '-'}
                    </td>
                    <td className="px-5 py-3">
                      {a.role ? (
                        <Badge className="bg-purple-100 text-purple-800 border-0 flex items-center gap-1 w-fit">
                          <ShieldCheck size={12} />{a.role.name}
                        </Badge>
                      ) : (
                        <span className="text-gray-400 text-sm">— No role —</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <Badge className={`${a.status ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} border-0`}>
                        {a.status ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" data-testid={`edit-admin-${a.id}`} onClick={() => openEdit(a)}>
                          <Edit size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`toggle-admin-${a.id}`}
                          onClick={() => toggleStatus(a)}
                          disabled={isSelf}
                          title={isSelf ? "You can't deactivate your own account" : ''}
                        >
                          {a.status ? <Lock size={14} /> : <ShieldCheck size={14} />}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          data-testid={`delete-admin-${a.id}`}
                          onClick={() => remove(a)}
                          disabled={isSelf}
                          title={isSelf ? "You can't delete your own account" : ''}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <p className="text-center text-gray-500 py-10">No admins match your filters</p>
        )}
      </div>
    </div>
  );
};

export default Admins;
